import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import GlobalError from '../components/GlobalError';
import { useForm } from 'react-hook-form';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductImages from '../components/ProductImages';
import { formatCurrency } from '../utils/format';
import {
  Typography, Table, TableBody, TableCell, TableRow, Box, FormHelperText, Chip,
  Button, Stack, Card, CardMedia, CardContent, CardActionArea, TextField, InputAdornment
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
const ProductDetailPage = ({ login }) => {
  const { id } = useParams();
  const [productData, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      quantity: 1,
    }
  });
  // 商品データを取得する関数
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // まずユーザー用を試す
        const privateResponse = await axiosInstance.get(`/product/${id}/user`, { withCredentials: true });
        setProduct(privateResponse.data);
      } catch (error) {
        if (error.response?.status === 401) {
          try {
            const publicResponse = await axiosInstance.get(`/product/${id}`);
            setProduct(publicResponse.data);
          } catch (err) {
            setError('商品情報の取得に失敗しました');
            setProduct(null);
          }
        } else {
          console.error('取得失敗:', error);
          setError('商品情報の取得に失敗しました');
          setProduct(null);
        }
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (productData?.variants?.length > 0) {
      setSelectedVariant(productData.variants[0]);
    }
  }, [productData]);

  const onSubmit = (data) => {
    if (!selectedVariant) return;
    const quantity = Number(data.quantity);
    const cartItems = [{
      product_id: productData.product_id,
      variant_id: selectedVariant.variant_id,
      quantity: quantity
    }];

    axiosInstance.post('/cart/add', { cartItems })
      .then(() => {
        setMessage(
          <>
            <Link to="/cart">カート</Link>に追加されました
          </>
        );
        reset({ quantity: 1 });
      })
      .catch(() => {
        setMessage('カートに追加できませんでした');
      });
  };

  const handleDownload = async () => {
    if (!productData.catalog_pdf_url) return;

    const url = `${process.env.REACT_APP_IMAGE_BASE_URL}${productData.catalog_pdf_url}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = productData.catalog_pdf_url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("ダウンロード失敗", err);
    }
  };

  const displayImages = selectedVariant?.image_url
    ? [
      {
        image_url: selectedVariant.image_url,
        image_order: selectedVariant.image_order,
      },
      ...productData?.images,
    ]
    : productData?.images;
  if (error) return <GlobalError errorMessage={error} />;

  if (!productData) {
    return <div>商品情報が見つかりません</div>;
  }
  return (
    <Box>
      {productData?.category_id && (
        <Breadcrumbs categoryId={productData.category_id} className="breadcrumb" />
      )}
      <Stack direction={{ md: "column", lg: "row" }} columnGap={6} rowGap={2} alignItems="flex-start">
        <Box
          flex={{ md: '1 1 100%', lg: '0 0 500px' }}
          width="100%"
          sx={{
            position: {
              lg: 'sticky', top: 60, alignSelf: 'flex-start'
            },
          }}
        >
          <ProductImages images={displayImages ?? []} />
        </Box>
        <Box flex="1 1 0" width="100%">
          <Typography variant='body2'>{selectedVariant?.sku}</Typography>
          <Typography variant='h2' mt={2}>{productData?.product_name ?? ''}
            {Object.entries(selectedVariant?.option_combination ?? {}).map(([type, value]) => (
              <React.Fragment key={type}> {type}: {value} </React.Fragment>
            ))}
          </Typography>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            {/* 価格 */}
            {selectedVariant?.selling_price !== undefined && selectedVariant?.selling_price !== null ? (
              <Typography fontSize='1.3rem' fontWeight={600}>{formatCurrency(selectedVariant?.selling_price ?? 0)}
                <Typography variant="caption" color="text.secondary">（税込{formatCurrency(selectedVariant?.selling_price_including_tax ?? 0)}）</Typography>
              </Typography>
            ) : (
              <>
                <Typography fontSize='1.3rem' fontWeight={600}>****** 円</Typography>
                <Typography my={2}>価格等の詳細情報を表示するには<Link to='/login'>新規登録またはログイン</Link>が必要です。</Typography>
                <Button variant="contained" color="primary" size='large' onClick={login}>ログイン／新規登録</Button>
              </>
            )}
            {selectedVariant?.selling_price !== undefined && selectedVariant?.selling_price !== null && (
              <Stack direction="row" spacing={1} mt={1}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    window.open(
                      `${process.env.REACT_APP_IMAGE_BASE_URL}${productData.catalog_pdf_url}`,
                      "_blank"
                    )
                  }
                  disabled={!productData?.catalog_pdf_url}
                  size='small'
                >
                  カタログ閲覧
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleDownload}
                  disabled={!productData.catalog_pdf_url}
                  size='small'
                >
                  カタログDL
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => navigate("/contact")}
                  size='small'
                >
                  お問い合わせ
                </Button>
              </Stack>
            )}
            {/* 情報 */}
            {selectedVariant?.selling_price !== undefined && selectedVariant?.selling_price !== null && (
              <>
                <Box sx={{ display: 'flex', direction: 'row', flexWrap: 'wrap', pb: 2, my: 2, gap: 3 }}>
                  {productData?.ec_setting?.show_stock && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">在庫</Typography>
                      <Typography variant="body2">{selectedVariant?.stock_status ?? '-'}</Typography>
                    </Box>
                  )}
                  {selectedVariant?.min_order_quantity > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">最低注文数</Typography>
                      <Typography variant="body2">{selectedVariant.min_order_quantity} 個</Typography>
                    </Box>
                  )}
                  {productData?.ec_setting?.show_delivery_time && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">発送までの日数</Typography>
                      <Typography variant="body2">{selectedVariant?.delivery_days ?? '-'}日</Typography>
                    </Box>
                  )}
                </Box>
                <Stack direction="row" gap={1.5} mt={2} alignItems="center">
                  <TextField
                    label="数量"
                    type="text"
                    variant="outlined"
                    size="large"
                    sx={{ width: '100%', maxWidth: 140, '& input': { textAlign: 'center' } }}
                    error={!!errors.quantity}
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">個</InputAdornment>,
                    }}
                    {...register('quantity', {
                      required: '数量は必須です',
                      validate: (value) => {
                        // 空チェック
                        if (!value) return '数量は必須です';
                        // 数字チェック
                        if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                        if (value.length > 6) return '6桁以内で入力してください';
                        if (Number(value) > 999999) return '999999以下で入力してください';
                        // 最低注文数チェック
                        const numValue = Number(value);
                        const minQty = selectedVariant?.min_order_quantity ?? 1;
                        if (numValue < minQty) return `最低注文数は ${minQty} 個です`;
                        return true;
                      },
                    })}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<AddShoppingCartIcon />}
                    size='large'
                  >カートに入れる
                  </Button>
                </Stack>
                {errors.quantity && (<FormHelperText error>{errors.quantity.message}</FormHelperText>)}
                {message &&
                  <Stack direction="row" spacing={1} mt={2}>
                    <CheckCircleIcon color="success" fontSize="small" /><Typography variant='body1'>{message}</Typography>
                  </Stack>
                }
              </>
            )}
            {/* バリエーション */}
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={2}
              mt={3}
            >
              {(productData?.variants ?? []).map((variant) => (
                <Card variant='outlined'
                  key={variant.variant_id}
                  onClick={() => setSelectedVariant(variant)}
                  sx={{
                    borderColor: selectedVariant?.variant_id === variant.variant_id
                      ? 'primary.main'
                      : '',
                    width: {
                      xs: '100%',
                      sm: 'calc(50% - 8px)',
                      lg: '100%',
                      xl: 'calc(50% - 8px)',
                    },

                  }}
                >
                  <CardActionArea onClick={() => setSelectedVariant(variant)} sx={{ width: '100%' }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ p: 1.5 }}
                    >
                      <CardMedia
                        component="img"
                        image={`${process.env.REACT_APP_IMAGE_BASE_URL}${variant.image_url}`}
                        alt={variant.sku}
                        className="product-image"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`;
                        }}
                        sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 1, }}
                      />
                      < CardContent sx={{ p: 1 }}>
                        {variant.option_combination && Object.entries(variant.option_combination).map(([type, value]) => (
                          <Typography key={type}>{type}: {value}</Typography>
                        ))}
                        {variant?.selling_price !== undefined && variant?.selling_price !== null && (
                          <>
                            <Typography>{formatCurrency(variant.selling_price)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              税込{formatCurrency(variant.selling_price_including_tax)}
                            </Typography><br />
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary">{variant.sku}</Typography>
                      </CardContent>
                    </Stack>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </form>
        </Box >
      </Stack >
      <Typography variant='h3' mt={6}>商品概要及び特徴</Typography>
      <Typography style={{ whiteSpace: 'pre-line' }}>{productData?.product_description ?? '-'}</Typography>
      {
        (productData?.specs?.length ?? 0) > 0 && (
          <div style={{ maxWidth: '800px' }}>
            <Typography variant="h3" mt={8}>
              スペック
            </Typography>

            <Table>
              <TableBody>
                {productData.specs.map(spec => (
                  <TableRow key={spec.spec_key}>
                    <TableCell variant="labelCell" sx={{ verticalAlign: 'top' }}>{spec.spec_key}</TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>{spec.spec_value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      }

    </Box>
  );
};

export default ProductDetailPage;
